import { useCallback, useEffect, useMemo, useState } from 'react';
import { PrimaryButton, Stack } from '@fluentui/react';
import { PageHeader } from '../core/PageHeader';
import { ListItem } from './components/ListItem';
import { useLocation } from 'react-router-dom';
import { fetchAccount, Patient } from '../../api';
import SelectService from './steps/SelectService';
import { SelectLocation } from './steps/SelectLocation';
import { CartProvider, useCart } from './CartContext';
import { PatientVisitsProvider } from './PatientVisitsContext';
import { SelectTime } from './steps/SelectTime';
import { SelectPatient } from './steps/SelectPatient';
import { ReviewAndBook } from './steps/ReviewAndBook';
import moment from 'moment';

interface Props {
  accountId?: string;
  patientId?: string;
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function CreateVisit() {
  const query = useQuery();
  const accountId = query.get('uid') || undefined;
  const patientId = query.get('pid') || undefined;

  return (
    <CartProvider>
      <PatientVisitsProvider>
        <CreateVisitInternal accountId={accountId} patientId={patientId} />
      </PatientVisitsProvider>
    </CartProvider>
  );
}

function CreateVisitInternal({ accountId, patientId }: Props) {
  const [requiresSubServiceCode, setRequiresSubServiceCode] = useState(false);
  const { state: cart, dispatch } = useCart();

  const setStep = useCallback(
    (step: number) => {
      dispatch({ type: 'set_step', data: step });
    },
    [dispatch]
  );

  useEffect(() => {
    let canceled = false;
    const aid = cart.who.accountId || accountId;
    if (aid) {
      fetchAccount(aid).then((account) => {
        if (!canceled) {
          let pid = cart.who.patientId || patientId;
          let patient = account.patients.find((p: Patient) => p.patientId === pid);
          if (!pid) {
            patient = account.patients.find((p: Patient) => p.mainPatientOfAccount);
            pid = patient?.patientId;
          }
          dispatch({ type: 'set_who', data: { accountId: aid, patientId: pid, account: account, patient: patient } });
        }
      });
    }
    return () => {
      canceled = true;
    };
  }, [dispatch, setStep, accountId, patientId, cart.who.accountId, cart.who.patientId]);

  const renderStep = useMemo(() => {
    switch (cart.step) {
      case 1:
        return <SelectPatient />;
      case 2:
        return <SelectLocation />;
      case 3:
        return <SelectService onServiceSelected={setRequiresSubServiceCode} />;
      case 4:
        return <SelectTime />;
      case 5:
        return <ReviewAndBook />;
      default:
        return 'you should not be here...';
    }
  }, [cart.step]);

  const currentStepComplete = useMemo(() => {
    switch (cart.step) {
      case 1:
        return !!cart.who.patientId && !!cart.who.patient;
      case 2:
        return !!cart.where.addressId;
      case 3:
        return (
          !!cart.what.serviceCode &&
          (!cart.what.symptomsRequired || (cart.what.symptomsRequired && cart.what.symptoms?.length > 3)) &&
          (!requiresSubServiceCode || (requiresSubServiceCode && !!cart.what.subServiceCode))
        );
      case 4:
        return !!cart.when.timeSlotId;
    }
    return false;
  }, [cart, requiresSubServiceCode]);

  return (
    <>
      <PageHeader title="Create Visit" />
      <Stack tokens={{ childrenGap: 16 }} horizontal>
        <Stack root={{ style: { width: 400 } }}>
          <ListItem
            title="Select Patient"
            iconName="Contact"
            value={cart.who.patient?.fullName}
            value2={cart.who.patient?.dateOfBirth}
            active={cart.step === 1}
            enabled={!cart.creating}
            completed={!!cart.who.patientId}
            testId="select_patient"
            onClick={() => setStep(1)}
          />
          <ListItem
            title="Select Location"
            iconName="MapPin"
            value={cart.where.address}
            active={cart.step === 2}
            enabled={!cart.creating && !!cart.who.patient}
            completed={!!cart.where.addressId}
            testId="select_location"
            onClick={() => setStep(2)}
          />
          <ListItem
            title="Select Service"
            iconName="Medical"
            value={cart.what.serviceName}
            value2={cart.what.symptoms}
            active={cart.step === 3}
            enabled={!cart.creating && !!cart.where.addressId}
            completed={!!cart.what.serviceCode && (!cart.what.symptomsRequired || (cart.what.symptomsRequired && cart.what.symptoms?.length > 3))}
            testId="select_service"
            onClick={() => setStep(3)}
          />
          <ListItem
            title="Select Time"
            iconName="DateTime"
            value={cart.when.timeSlot}
            value2={cart.when.date ? moment(cart.when.date).format('MM/DD/YYYY') : ''}
            active={cart.step === 4}
            enabled={
              !cart.creating &&
              !!cart.what.serviceCode &&
              (!cart.what.symptomsRequired || (cart.what.symptomsRequired && cart.what.symptoms?.length > 3)) &&
              (!requiresSubServiceCode || (requiresSubServiceCode && !!cart.what.subServiceCode))
            }
            completed={!!cart.when.timeSlotId}
            testId="select_time"
            onClick={() => setStep(4)}
          />
          <ListItem
            title="Review and Book"
            iconName="CheckList"
            active={cart.step === 5}
            enabled={!cart.creating && !!cart.when.timeSlotId}
            completed={false}
            testId="select_review"
            onClick={() => setStep(5)}
          />
          {cart.step < 5 && (
            <PrimaryButton style={{ width: '100%', height: 50 }} onClick={() => setStep(cart.step + 1)} text="Next Step" disabled={!currentStepComplete} data-tid="btn_next" />
          )}
        </Stack>
        <Stack grow={1} style={{ width: 0 }}>
          {renderStep}
        </Stack>
      </Stack>
    </>
  );
}
